import type { PortalOrganisation, PortalSession } from "@/types/portal";

/**
 * The slug used to identify the platform organisation (Larasoft).
 * Matches the constant in the control plane (theme.py PLATFORM_ORG_SLUG).
 */
export const PLATFORM_ORG_SLUG = "larasoft";

/**
 * Check if the given organisation is the platform organisation.
 */
export function isPlatformOrg(org: PortalOrganisation | undefined): boolean {
  return org?.slug === PLATFORM_ORG_SLUG;
}

/**
 * Role names as returned by the backend auth service.
 * Lower roleLevel = higher privileges.
 */
export const ROLES = {
  SUPER_ADMIN: "SuperAdmin",
  ADMIN: "Admin",
  ORG_ADMIN: "OrgAdmin",
  EDITOR: "Editor",
  USER: "User",
  VIEWER: "Viewer",
} as const;

export type RoleName = (typeof ROLES)[keyof typeof ROLES];

/**
 * Role hierarchy levels. Lower number = higher privileges.
 * Used for numeric comparison: user.roleLevel <= ROLE_LEVELS.ADMIN grants admin access.
 */
export const ROLE_LEVELS = {
  SUPER_ADMIN: 1,
  ADMIN: 2,
  EDITOR: 3,
  USER: 4,
  VIEWER: 5,
} as const;

export type RoleLevel = (typeof ROLE_LEVELS)[keyof typeof ROLE_LEVELS];

/**
 * Check if the session has at least the specified role level.
 * Lower roleLevel = higher privileges, so we check <= minLevel.
 */
export function hasMinRoleLevel(
  session: PortalSession | null,
  minLevel: number,
): boolean {
  if (!session?.user?.roleLevel) return false;
  return session.user.roleLevel <= minLevel;
}

/**
 * Check if the session user is a SuperAdmin.
 */
export function isSuperAdmin(session: PortalSession | null): boolean {
  return hasMinRoleLevel(session, ROLE_LEVELS.SUPER_ADMIN);
}

/**
 * Check if the session user is at least an Admin (includes SuperAdmin).
 */
export function isAdmin(session: PortalSession | null): boolean {
  return hasMinRoleLevel(session, ROLE_LEVELS.ADMIN);
}

/**
 * Check if the session user is at least an Editor (includes SuperAdmin, Admin).
 */
export function isEditor(session: PortalSession | null): boolean {
  return hasMinRoleLevel(session, ROLE_LEVELS.EDITOR);
}

/**
 * Check if the session user has the exact role name (case-insensitive).
 */
export function hasRole(
  session: PortalSession | null,
  roleName: string,
): boolean {
  if (!session?.user?.role) return false;
  return session.user.role.toLowerCase() === roleName.toLowerCase();
}

/**
 * Check if the session user has any of the specified roles (case-insensitive).
 */
export function hasAnyRole(
  session: PortalSession | null,
  roleNames: string[],
): boolean {
  if (!session?.user?.role) return false;
  const userRole = session.user.role.toLowerCase();
  return roleNames.some((name) => userRole === name.toLowerCase());
}

/**
 * Get a human-readable role display name.
 */
export function getRoleDisplayName(session: PortalSession | null): string {
  if (!session?.user?.role) return "Unknown";
  return session.user.role;
}

/**
 * Feature access flags based on role.
 * Use these for consistent feature gating across the application.
 */
export function getFeatureAccess(session: PortalSession | null) {
  return {
    canRefreshConnectors: isSuperAdmin(session),
    canEditConnectorProperties: isAdmin(session),
    canViewConnectors: hasMinRoleLevel(session, ROLE_LEVELS.USER),
    canManageUsers: isSuperAdmin(session),
    canAccessAdminDashboard: isSuperAdmin(session),
    canDeleteResources: isSuperAdmin(session),
  };
}
